/**
 * What the world says happened.
 *
 * An event is a tuple — tick, type, who, what — not a sentence. The sentence
 * is a *rendering* of the tuple, shipped alongside it for MVP convenience
 * (`@annwn/protocol`'s `LogEvent` is this minus the delivery fields), and the
 * tuple is what gets archived. That split is why "how did Gwion die" is still
 * answerable three balance passes after every narration template was rewritten.
 *
 * These live in the sim rather than in `@annwn/protocol` for the same reason
 * the intent vocabulary does: an event is a statement about the world, and the
 * world is what decides what may be said about it. The protocol package
 * re-exports the vocabulary so client, server and archive all spell `said` the
 * same way.
 *
 * What the sim adds beyond the archived tuple is **who should be told**. That
 * is a rule, not a transport detail: "words carry twelve tiles" (design spec
 * §8) is a fact about the world, and a server that decided it independently
 * would be a second rules engine nobody was maintaining.
 */
/**
 * Every kind of thing worth writing down. Appended to by later milestones —
 * M1-06 adds the wound vocabulary, M2 the combat exchange. Never renamed: old
 * rows in the archive spell them this way.
 */
export const SIM_EVENT_TYPES = [
    'joined',
    'left',
    'moved',
    'said',
    'examined',
    'blocked',
    // M2. `attacked` is the commitment — archived rather than narrated, because
    // the *reading* of a wind-up is the opponent's glyph on the client, not a
    // line of prose forty times a fight. `blow` is the resolution and carries the
    // whole M1-06 chain; `status` is what the clocks did about it.
    'attacked',
    'guarded',
    'blow',
    'status',
    'died',
    // M2-07. One type for every verb against a helpless body — staunch, loot,
    // drag — with the verb in `refs[0]`. One type rather than four because the
    // archive's question is "what was done to this body, and by whom", and an
    // answer split across four event names is one every future reader has to
    // remember to union.
    'interacted',
    // M3-07. A heartbeat in the journal rather than something that happened in
    // the world: the tick, the population and what the sky was doing, written
    // down on a fixed cadence whether or not anybody was playing.
    //
    // It exists because the journal's other rows only appear when somebody does
    // something, so an empty valley leaves a gap in the archive that is
    // indistinguishable from a server that was switched off. Answering "what
    // happened while nobody was on?" — including "nothing, and here is the proof
    // it was running" — needs a row that does not depend on a player.
    'world-tick',
    // RC2-01. The core loop's mini-quests, as archive-only telemetry: an errand
    // offered, one closed off a real world event, or the first ring graduated.
    // `refs[0]` is the phase (`offered` / `completed` / `graduated`) and `refs[1]`
    // the errand id where there is one. Nothing player-facing rides this yet —
    // the HUD line (RC2-07) and Journal (RC2-03) are their own tickets; this is
    // the row RC2-10 counts to answer "did they run the loop?".
    'errand',
    // RC2-02. The "you are getting better" beat, fired once when a practice tally
    // crosses into a new banded rating (`practice.ts`). `refs` is
    // `['better', familiarity, rating]`; `audience` is `actor`, because it is a
    // fact about *you*. Unlike `errand` this one is meant to be seen — but its
    // producer (the tally seam in `advance`'s `emit`) wires alongside RC2-01's, so
    // nothing emits it yet; the type exists so the tally engine can name it.
    'practice',
];
/**
 * Who is told about an event. Distance culling for the log is the same
 * question area-of-interest asks about entities, and answering it in two
 * places is how a player learns that somebody joined on the far side of the
 * valley (M0-04's leak, arriving through a different door).
 *
 * - `archive` — recorded, shown to nobody. Movement: forty rows a second of
 *   perfectly good telemetry that would drown a log pane.
 * - `witnesses` — anyone who can see `at`, within their area of interest.
 * - `earshot` — anyone within `range` tiles of `at`. Speech.
 * - `actor` — `to` and nobody else. The answer to a question you asked.
 * - `world` — everybody, wherever they are standing, and carrying no `at` at
 *   all. Added in M3-05 for the one thing that is genuinely true of the whole
 *   commote at once: the sky. Rain arriving is not an event with a location,
 *   and delivering it as `witnesses` at some invented coordinate would have
 *   told half the valley it was raining and left the other half dry.
 */
export const EVENT_AUDIENCES = ['archive', 'witnesses', 'earshot', 'actor', 'world'];
/**
 * Fill in the empty halves so every event has one shape.
 *
 * `tick` is supplied by `advance` rather than by the caller: a handler that
 * could stamp its own tick could stamp the wrong one, and an event in the
 * archive with a tick the world never simulated is worse than no event.
 */
export function simEvent(tick, init) {
    return {
        tick,
        type: init.type,
        actors: init.actors ?? [],
        refs: init.refs ?? [],
        ...(init.text === undefined ? {} : { text: init.text }),
        audience: init.audience ?? 'witnesses',
        at: init.at ?? null,
        to: init.to ?? null,
        range: init.range ?? 0,
    };
}
export function isSimEventType(value) {
    return typeof value === 'string' && SIM_EVENT_TYPES.includes(value);
}
//# sourceMappingURL=event.js.map